import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { CreditCard, WarningOctagon, X, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

/** Red strip for tenant admins when the last Stripe renewal failed. Data comes from /auth/me (subscription_status + billing_alert). */
export default function BillingAlertBanner() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("fixli_billing_alert_dismissed") === "1");
  const [busy, setBusy] = useState(false);
  const alert = user?.billing_alert;
  const dunning = user?.dunning || {};
  const pastDue = user?.subscription_status === "past_due" || alert?.type === "payment_failed";
  const locked = !!dunning.locked;
  if (!user || user.is_platform_admin || !pastDue) return null;
  const isAdmin = ["super_admin", "admin"].includes(user.role);
  if (!isAdmin && !locked) return null;
  if (hidden && !locked) return null;

  const failedOn = alert?.failed_at ? new Date(alert.failed_at).toLocaleDateString("en-GB") : null;
  const retryOn = alert?.next_attempt_at ? new Date(alert.next_attempt_at).toLocaleDateString("en-GB") : null;
  const amount = alert?.amount != null ? `£${Number(alert.amount).toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : null;

  const updateCard = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open the payment portal — try again from Billing");
      setBusy(false);
    }
  };

  const daysLeft = dunning.days_left;
  const countdown = locked
    ? null
    : daysLeft == null ? null
    : daysLeft <= 0 ? "Your workspace becomes read-only today."
    : `Your workspace becomes read-only in ${daysLeft} day${daysLeft === 1 ? "" : "s"}.`;

  return (
    <div className={`${locked ? "bg-red-800" : "bg-red-600"} text-white px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm shadow-md`} role="alert" data-testid="billing-alert-banner" data-locked={locked ? "1" : "0"}>
      {locked ? <LockKey size={20} weight="fill" className="shrink-0" /> : <WarningOctagon size={20} weight="fill" className="shrink-0" />}
      <p className="flex-1 min-w-[220px]" data-testid="billing-alert-text">
        {locked ? (
          <>
            <span className="font-semibold">{user.tenant_name || "This workspace"} is read-only.</span>{" "}
            The subscription payment{amount ? ` of ${amount}` : ""} is still outstanding{failedOn ? ` (declined ${failedOn})` : ""} — {isAdmin ? "update the card to restore full access instantly." : "ask an admin to update the card to restore full access."}
          </>
        ) : (
          <>
            <span className="font-semibold">Your card was declined{failedOn ? ` on ${failedOn}` : ""}{amount ? ` (${amount})` : ""}.</span>{" "}
            Update it to keep {user.tenant_name || "your workspace"} running{retryOn ? ` — Stripe will retry on ${retryOn}` : ""}.
            {countdown && <span className="ml-1 font-semibold underline decoration-red-200/70 underline-offset-2" data-testid="billing-alert-countdown">{countdown}</span>}
          </>
        )}
      </p>
      <div className="flex items-center gap-2">
        {isAdmin && (
          <Button size="sm" variant="secondary" className="h-8 bg-white text-red-700 hover:bg-red-50" onClick={updateCard} disabled={busy} data-testid="billing-alert-update-card">
            <CreditCard size={14} className="mr-1" /> {busy ? "Opening…" : "Update card"}
          </Button>
        )}
        {isAdmin && pathname !== "/platform/billing" && (
          <Button asChild size="sm" variant="ghost" className="h-8 text-white hover:bg-red-700 hover:text-white">
            <Link to="/platform/billing" data-testid="billing-alert-view-billing">View billing</Link>
          </Button>
        )}
        {!locked && (
          <button type="button" className="p-1 rounded hover:bg-red-700" onClick={() => { sessionStorage.setItem("fixli_billing_alert_dismissed", "1"); setHidden(true); }} aria-label="Dismiss for this session" data-testid="billing-alert-dismiss">
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
